/**
 * Custom element that provides various tab functionality for content
 * Can be used in three modes:
 * - fetch-tabs: Loads content dynamically via AJAX without page refresh
 * - redirect-tabs: Redirects to different pages with full page reload
 * - visibility-tabs: Shows/hides content sections within the current page
 *
 * @element content-tabs-v2
 */

if(!customElements.get('content-tabs-v2')) {
  class contentTabsV2 extends HTMLElement {
    /**
     * Initializes the tabs component and sets default properties
     * Stores initial page state for history navigation
     */
    constructor() {
      super();
      this.initialTab = null;
      this.initialClick = null;
      this.isFetchTabs = false;
      this.isRedirectTabs = false;
      this.isVisibilityTabs = false;
      this.initialContent = document.body.innerHTML; // Store initial content
      this.currentUrl = window.location.href; // Store the initial URL
      this.currentTabIndex = 0; // Track current tab for keyboard navigation
    }

    /**
     * Lifecycle callback when element is added to the DOM
     * Determines tab type, initializes tabs, and sets up event listeners
     * Adds browser history navigation support
     */
    connectedCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      this.tabName = this.getAttribute('data-tab-name');
      const tabParam = urlParams.get(`${this.tabName}-tab`);
      const clickParam = urlParams.get("click");
      this.initialTab = tabParam ?? null;
      this.initialClick = clickParam ?? null;
      this.updateUrl = this.hasAttribute('js-update-url');
      this.setTabType();
      this.initTabs();
      if (Shopify.designMode) {
        document.addEventListener("shopify:section:load", this.initTabs.bind(this));
      }
      window.addEventListener('popstate', this.handlePopState.bind(this)); // Listen for history changes
    }

    /**
     * Sets the tab type based on the element's class list
     * Ensures mutual exclusivity - only one tab type can be active at a time
     * Priority: fetch-tabs > redirect-tabs > visibility-tabs
     */
    setTabType() {
      this.isFetchTabs = this.classList.contains('fetch-tabs');
      this.isRedirectTabs = this.classList.contains('redirect-tabs');
      this.isVisibilityTabs = this.classList.contains('visibility-tabs');
      // we should only have one tab type. Check if one type is set, and if so set the other to false.
      if (this.isFetchTabs && (this.isRedirectTabs || this.isVisibilityTabs)) {
        this.isRedirectTabs = false;
        this.isVisibilityTabs = false;
      }
      if (this.isRedirectTabs && (this.isFetchTabs || this.isVisibilityTabs)) {
        this.isFetchTabs = false;
        this.isVisibilityTabs = false;
      }
      if (this.isVisibilityTabs && (this.isFetchTabs || this.isRedirectTabs)) {
        this.isFetchTabs = false;
        this.isRedirectTabs = false;
      }
    }

    /**
     * Sets up tab functionality and event listeners
     * Handles initial tab selection based on URL parameters
     * Initializes dropdown select for mobile view
     */
    initTabs() {
      this.tabsContainer = this.querySelector(".js-custom-tabs");
      this.contentTabsLinks = this.tabsContainer.querySelectorAll(".content-tabs__tab");
      this.contentTabsSelect = this.querySelector('.content-tabs__select');
      const contentTabsSelectActive = this.contentTabsSelect?.querySelector('.content-tabs__select-active');

      // Add ARIA attributes for accessibility
      this.setupAriaAttributes(this.tabsContainer, this.contentTabsLinks);

      this.contentTabsLinks.forEach((tabLink, index) => {
        let isLoaded = false; // Flag to prevent duplicate loads

        /**
         * Handles tab activation and content loading
         * Prevents multiple simultaneous loads of the same tab
         * Routes to appropriate handler based on tab type
         *
         * @param {Event} event - The triggering event (click or focus)
         */
        const loadTabContent = (event) => {
          event.preventDefault();

          if (isLoaded) return; // Prevent running the handler again if already loaded
          isLoaded = true;

          // Update current tab index
          this.currentTabIndex = index;

          // Update mobile select display
          this.updateMobileSelectDisplay(tabLink);

          // Tab action will change depending on the tab type.
          if (this.isFetchTabs) {
            this.loadContent(tabLink).finally(() => {
              isLoaded = false;
            });
          } else if(this.isRedirectTabs) {
            this.redirectPage(tabLink);
            isLoaded = false;
          } else if(this.isVisibilityTabs) {
            this.switchTab(tabLink);
            isLoaded = false;
          }
        };

        // Attach event listeners for 'click' only (focus will be handled by keyboard navigation)
        tabLink.addEventListener("click", (event) => {
          this.activateTab(event, loadTabContent, this.tabsContainer);
        });

        // Add keyboard navigation
        tabLink.addEventListener("keydown", (event) => {
          this.handleKeyboardNavigation(event, this.contentTabsLinks, index, loadTabContent);
        });
      });

      // Initialize the first tab or the one specified by the URL
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get(`${this.tabName}-tab`);
      let targetTab = this.tabsContainer.querySelector("li.active .content-tabs__tab");

      if(!targetTab){
        targetTab = this.contentTabsLinks[0];
      }

      if (tabParam) {
        const tempTargetTab = this.tabsContainer.querySelector(`.content-tabs__tab[data-target="${this.tabName}-${tabParam}"]`);
        if(tempTargetTab) {
          targetTab = tempTargetTab;
          // Update current tab index
          this.currentTabIndex = Array.from(this.contentTabsLinks).indexOf(targetTab);
        }
      }

      if (targetTab) {
        if (this.isVisibilityTabs) targetTab.click();
        if (contentTabsSelectActive) contentTabsSelectActive.textContent = targetTab.textContent;
      }

      if (this.contentTabsSelect) {
        this.contentTabsSelect.addEventListener('click', () => {
          this.tabsContainer.classList.toggle('open');
        });

        // Add keyboard support for the select trigger
        this.contentTabsSelect.addEventListener('keydown', (event) => {
          const firstTab = this.contentTabsLinks[0];

          switch (event.key) {
            case 'Enter':
            case ' ':
            case 'ArrowDown':
            case 'ArrowUp':
              event.preventDefault();
              this.tabsContainer.classList.add('open');
              // Focus the first tab when dropdown opens
              if (firstTab) {
                firstTab.focus();
                this.currentTabIndex = 0;
              }
              break;
          }
        });

        // Make the select trigger focusable
        if (!this.contentTabsSelect.hasAttribute('tabindex')) {
          this.contentTabsSelect.setAttribute('tabindex', '0');
        }

        this.appendChild(this.contentTabsSelect);
      }

      this.handleClickUrlParam(this.initialClick);
    }

    /**
     * Sets up ARIA attributes for better accessibility
     * @param {HTMLElement} tabsContainer - The container element for tabs
     * @param {NodeList} contentTabsLinks - All tab link elements
     */
    setupAriaAttributes(tabsContainer, contentTabsLinks) {
      // Set role="tablist" on the container
      tabsContainer.setAttribute('role', 'tablist');

      contentTabsLinks.forEach((tabLink, index) => {
        // Set ARIA attributes on each tab
        tabLink.setAttribute('role', 'tab');
        tabLink.setAttribute('tabindex', index === this.currentTabIndex ? '0' : '-1');
        tabLink.setAttribute('aria-selected', index === this.currentTabIndex ? 'true' : 'false');

        // Set up tab panel relationships
        const targetId = tabLink.getAttribute('data-target');
        const tabPanel = document.querySelector(`.js-content-tab[data-tab-id="${targetId}"]`);

        if (tabPanel) {
          tabPanel.setAttribute('role', 'tabpanel');
          // Tab panels should not be in the tab order - remove or set to -1
          tabPanel.setAttribute('tabindex', '-1');
          tabPanel.setAttribute('aria-labelledby', tabLink.id || `tab-${index}`);

          if (!tabLink.id) {
            tabLink.id = `tab-${index}`;
          }

          if (!tabPanel.id) {
            tabPanel.id = `panel-${index}`;
          }

          tabLink.setAttribute('aria-controls', tabPanel.id);
        }
      });
    }

    /**
     * Handles keyboard navigation for tabs
     * @param {KeyboardEvent} event - The keyboard event
     * @param {NodeList} contentTabsLinks - All tab link elements
     * @param {number} currentIndex - Current tab index
     * @param {Function} loadTabContent - Function to load tab content
     */
    handleKeyboardNavigation(event, contentTabsLinks, currentIndex, loadTabContent) {
      // Check if we're in mobile dropdown mode
      const isDropdownOpen = this.tabsContainer && this.tabsContainer.classList.contains('open');
      const isMobileMode = this.contentTabsSelect && window.getComputedStyle(this.contentTabsSelect).display !== 'none';

      // Define key actions for different modes
      const keyActions = {
        mobile: {
          'Tab': (event) => this.handleTabNavigation(event, contentTabsLinks, currentIndex),
          'ArrowDown': () => this.navigateToTab(contentTabsLinks, currentIndex + 1),
          'ArrowUp': () => this.navigateToTab(contentTabsLinks, currentIndex - 1),
          'Enter': (event) => this.activateTab(event, loadTabContent, this.tabsContainer),
          ' ': (event) => this.activateTab(event, loadTabContent, this.tabsContainer),
          'Escape': () => this.closeDropdown(this.tabsContainer, this.contentTabsSelect)
        },
        desktop: {
          'ArrowRight': () => this.navigateToTab(contentTabsLinks, currentIndex + 1),
          'ArrowDown': () => this.navigateToTab(contentTabsLinks, currentIndex + 1),
          'ArrowLeft': () => this.navigateToTab(contentTabsLinks, currentIndex - 1),
          'ArrowUp': () => this.navigateToTab(contentTabsLinks, currentIndex - 1),
          'Enter': (event) => this.activateTab(event, loadTabContent),
          ' ': (event) => this.activateTab(event, loadTabContent),
          'Home': () => this.navigateToTab(contentTabsLinks, 0, true),
          'End': () => this.navigateToTab(contentTabsLinks, contentTabsLinks.length - 1, true)
        }
      };

      // Select the appropriate key map
      const currentKeyMap = (isMobileMode && isDropdownOpen) ? keyActions.mobile : keyActions.desktop;
      const action = currentKeyMap[event.key];

      if (action) {
        event.preventDefault();
        action(event);
      }
    }

    /**
     * Handles Tab key navigation in mobile dropdown mode
     * @param {KeyboardEvent} event - The keyboard event
     * @param {NodeList} contentTabsLinks - All tab link elements
     * @param {number} currentIndex - Current tab index
     */
    handleTabNavigation(event, contentTabsLinks, currentIndex) {
      const direction = event.shiftKey ? -1 : 1;
      this.navigateToTab(contentTabsLinks, currentIndex + direction);
    }

    /**
     * Navigates to a specific tab by index
     * @param {NodeList} contentTabsLinks - All tab link elements
     * @param {number} targetIndex - Target tab index
     * @param {boolean} absolute - If true, use exact index; if false, wrap around
     */
    navigateToTab(contentTabsLinks, targetIndex, absolute = false) {
      let finalIndex;

      if (absolute) {
        finalIndex = Math.max(0, Math.min(targetIndex, contentTabsLinks.length - 1));
      } else {
        finalIndex = ((targetIndex % contentTabsLinks.length) + contentTabsLinks.length) % contentTabsLinks.length;
      }

      this.focusTab(contentTabsLinks, finalIndex);
    }

    /**
     * Activates a tab and optionally closes dropdown
     * @param {KeyboardEvent} event - The keyboard event
     * @param {Function} loadTabContent - Function to load tab content
     * @param {HTMLElement} tabsContainer - Optional container to close dropdown
     */
    activateTab(event, loadTabContent, tabsContainer = null) {
      loadTabContent(event);
      if (tabsContainer) {
        tabsContainer.classList.remove('open');

        // Update the mobile select display text
        this.updateMobileSelectDisplay(event.target);
      }
    }

    /**
     * Closes the dropdown and returns focus to trigger
     * @param {HTMLElement} tabsContainer - The tabs container
     * @param {HTMLElement} contentTabsSelect - The select trigger element
     */
    closeDropdown(tabsContainer, contentTabsSelect) {
      tabsContainer.classList.remove('open');
      if (contentTabsSelect) {
        contentTabsSelect.focus();
      }
    }

    /**
     * Focuses a specific tab and updates ARIA attributes
     * @param {NodeList} contentTabsLinks - All tab link elements
     * @param {number} targetIndex - Index of tab to focus
     */
    focusTab(contentTabsLinks, targetIndex) {
      // Update ARIA attributes
      contentTabsLinks.forEach((tab, index) => {
        tab.setAttribute('tabindex', index === targetIndex ? '0' : '-1');
        tab.setAttribute('aria-selected', index === targetIndex ? 'true' : 'false');
      });

      // Focus the target tab
      contentTabsLinks[targetIndex].focus();
      this.currentTabIndex = targetIndex;
    }

    /**
     * Handles the 'click' URL parameter by triggering a click on the element with the specified ID
     * Called after tab content is loaded and visible
     */
    handleClickUrlParam(clickSelector) {
      if (!clickSelector) return;

      setTimeout(() => {
        const elementToClick = document.getElementById(clickSelector);
        if (elementToClick) {
          elementToClick.click();
          // Optionally scroll to the element
          elementToClick.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Remove the click parameter from URL after action is performed
        const url = new URL(window.location);
        url.searchParams.delete("click");
        window.history.replaceState({}, "", url);
      }, 100);
    }

    /**
     * Redirects the browser to the URL specified in the tab's href attribute
     * Used for redirect-tabs mode
     *
     * @param {HTMLAnchorElement} tabLink - The tab link element to get the target URL from
     */
    redirectPage(tabLink) {
      const targetUrl = tabLink.getAttribute("href");
      window.location.href = targetUrl;
    }

    /**
     * Switches visibility between tab content sections
     * Updates URL parameters to reflect the active tab
     * Used for visibility-tabs mode
     * Updates browser history without page reload
     *
     * @param {HTMLAnchorElement} tabLink - The tab link element that was activated
     */
    switchTab(tabLink) {
      const contentTabSections = document.querySelectorAll(`.js-content-tab[data-tab-id^="${this.tabName}-"]`);

      // Deactivate all tabs and content sections
      this.tabsContainer.querySelectorAll("li").forEach((link) => {
        link.classList.remove("active");
      });

      contentTabSections.forEach((section) => {
        section.classList.remove("active");
      });

      // Update ARIA attributes for all tabs
      this.contentTabsLinks.forEach((tab) => {
        const isActive = tab === tabLink;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // Activate the clicked tab and corresponding content section
      const targetId = tabLink.getAttribute("data-target");
      const targetContent = document.querySelectorAll(`.js-content-tab[data-tab-id="${targetId}"]`);

      targetContent.forEach((section) => {
        section.classList.add("active");
      });

      tabLink.parentElement.classList.add("active");

      // Reset scroll position to top when switching tabs (if enabled via data attribute)
      if (this.hasAttribute('data-reset-scroll')) {
        this.resetScrollPosition();
      }

      // Remove the 'click' parameter as it should not persist
      // The click parameter will be used once and then removed
      const url = new URL(window.location);
      if (url.searchParams.has("click")) {
        url.searchParams.delete("click");
      }

      if (this.updateUrl) {
        // Update the URL parameter and push to browser history
        url.searchParams.set(`${this.tabName}-tab`, targetId.replace(`${this.tabName}-`, ""));
        window.history.pushState({}, "", url);
      }
    }

    /**
     * Fetches and loads content from a specified URL
     * Used for fetch-tabs mode to load content dynamically via AJAX
     * Handles security restrictions by checking origin
     * Updates browser history and document title
     * Executes scripts in the fetched content
     *
     * @param {HTMLAnchorElement} tabLink - The tab link element to get the target URL from
     * @returns {Promise<void>} A promise that resolves when content is loaded
     */
    async loadContent(tabLink) {
      let targetUrl = tabLink.getAttribute("href");
      const bodyElement = document.querySelector("#MainContent");
      if (targetUrl) {
        // Ensure the URL we want to fetch belongs to the store.
        if (targetUrl.startsWith("/")) {
          targetUrl = `${window.location.origin}${targetUrl}`;
        }
        if (new URL(targetUrl).origin === window.location.origin) {
          try {
            const response = await fetch(`${targetUrl}`);
            const html = await response.text();

            // Parse the HTML response to extract the body content
            const parser = new DOMParser();
            const fetchedDocument = parser.parseFromString(html, "text/html");
            const fetchedBody = fetchedDocument.querySelector("#MainContent");
            const fetchedTitle = fetchedDocument.querySelector("title");
            if (fetchedTitle) {
              const currentTitle = document.querySelector("title");
              if(currentTitle) currentTitle.innerHTML = fetchedTitle.innerHTML;
            }
            if (fetchedBody) {
              // Clear the existing body content
              bodyElement.innerHTML = '';

              // Insert the new body content
              bodyElement.insertAdjacentHTML('beforeend', fetchedBody.innerHTML);

              // Execute any scripts within the fetched content
              // This may cause console errors if particular scripts have already ran before, or attempt to redfine globals.
              // This code needs re-evaluation.
              // Unfortuantely, with this component and feature, Greyson can opt to load in any URL.
              const scriptTags = fetchedBody.querySelectorAll("script");
              scriptTags.forEach((script) => {
                const newScript = document.createElement("script");
                if (script.src) {
                  newScript.src = script.src; // Reuse the src for external scripts
                } else {
                  newScript.textContent = script.textContent; // Inline script
                }
                document.body.appendChild(newScript); // Append and execute
              });

              // Update the browser's URL without reloading the page
              // Save current content in history state
              const url = new URL(targetUrl);

              // Don't preserve the click parameter as it should only be used once
              window.history.pushState({ tabContent: bodyElement.innerHTML }, "", url.toString());
            } else {
              console.warn("No fetched body content");
            }
          } catch (error) {
            console.error("Failed to load content: ", error);
          }
        } else {
          console.error("Attempted to fetch a URL outside the current domain:", targetUrl);
        }
      }
    }

    /**
     * Handles browser back/forward navigation events
     * Restores page content from history state or from initial state
     * Re-initializes tabs after content restoration
     *
     * @param {PopStateEvent} event - The popstate event from browser navigation
     */
    handlePopState(event) {
      const state = event.state;
      if (state && state.tabContent) {
        document.body.innerHTML = state.tabContent; // Restore content from history state
        this.initTabs();  // Re-initialize tabs if necessary
      } else {
        // Restore the initial content if no state is found (i.e., the very first page load)
        document.body.innerHTML = this.initialContent;
        this.initTabs();  // Re-initialize tabs if necessary
      }
    }

    /**
     * Updates the mobile select display text
     * @param {HTMLAnchorElement} tabLink - The tab link element to update the display text
     */
    updateMobileSelectDisplay(tabLink) {
      const contentTabsSelectActive = this.contentTabsSelect?.querySelector('.content-tabs__select-active');
      if (contentTabsSelectActive && tabLink) {
        contentTabsSelectActive.textContent = tabLink.textContent;
      }
    }

    /**
     * Resets scroll position to top for the closest scrollable container
     * Looks for a scrollable container using data attribute
     */
    resetScrollPosition() {
      // Check if a specific scroll container is defined via data attribute
      const scrollContainerSelector = this.getAttribute('data-scroll-container');
      let scrollableContainer;

      if (scrollContainerSelector) scrollableContainer = document.querySelector(scrollContainerSelector) || this.closest(scrollContainerSelector);
      if (scrollableContainer && scrollableContainer.scrollTop !== undefined) scrollableContainer.scrollTop = 0;
    }
  }

  /**
   * Register the custom element with the browser
   * After registration, <content-tabs-v2> elements can be used in HTML
   */
  customElements.define("content-tabs-v2", contentTabsV2);
}